/**
 * Video Analysis Prompt Generators
 * 
 * This file contains all prompt generation functions for AI video analysis.
 * Each function represents a version of the prompt for tracking changes and testing.
 */

// Version 1: Original prompt (backed up on 2025-07-21)
export const createOriginalAnalysisPrompt = (language: 'en' | 'fr' = 'en'): string => {
  const prompts = {
    en: `You are a professional hockey goalie coach analyzing video footage to extract game data.`,
    fr: `Vous êtes un entraîneur professionnel de gardiens de but de hockey analysant des images vidéo pour extraire des données de match.`
  };
  
  const instructions = {
    en: {
      gameInfo: '1. **Game Information**',
      opponent: '- Opponent team name (if visible on jerseys/boards, otherwise "Unknown Team")',
      competition: '- Competition type: choose from "regular-season", "playoff", "tournament", "exhibition", or "practice"',
      performance: '2. **Performance Metrics**',
      shotsFaced: '- Total shots faced by the goalkeeper',
      saves: '- Number of saves made',
      goals: '- Calculate goals allowed (shots faced - saves)',
      outcome: '3. **Game Outcome**',
      result: '- Result: "win", "loss", "win-ot", "loss-ot", "win-so", "loss-so", or null if unclear',
      shotData: '4. **Shot Data with Timestamps and Analysis** - CRITICAL: Only analyze shots that actually occur in the video. Do not create fictional shots.\n\n**Video Length Guidelines:**\n- Short clips (under 30 seconds): Typically contain 1-3 actual shots maximum\n- Longer footage: May contain more shots, but only analyze those that clearly happen\n- Quality over quantity: Better to have fewer accurate shots than many inaccurate ones',
      timing: 'a) **Timing Information** (CRITICAL - provide exact video timestamps)',
      clipStart: '- clipStartTime: exact timestamp in MM:SS format when the shot attempt begins (e.g., "01:23", "05:47")',
      clipEnd: '- clipEndTime: exact timestamp in MM:SS format when the play ends (e.g., "01:28", "05:52")',
      noZero: '- DO NOT use "00:00" - provide the actual time in the video when each shot occurs',
      shotDetails: 'b) **Shot Details**',
      shotType: '- Shot type: choose from "Wrist Shot", "Slap Shot", "Snap Shot", "Backhand", "Deflection / Tip-in", "One-Timer", "Wraparound", "Rebound Shot"',
      gameSituation: '- Game situation: choose from "Break away", "Penalty Shot", "Screened Shot", "Odd-Man Rush", "Cross-Ice Pass", "Point Shot", "Net-Front Battle", "Shorthanded Goal", "Power Play Goal", "Turnover Leading to Goal", "Off the Rush", "Puck Battle / Scramble"',
      resultType: '- Result: "save" or "goal"',
      origin: '- Shot origin: x,y coordinates (0-100 scale, where 0,0 is top-left of rink from goalie\'s perspective)',
      destination: '- Shot destination: x,y coordinates (0-100 scale, where 0,0 is top-left of goal net)',
      analysis: 'c) **Detailed Goalie Analysis** for this specific shot:',
      technique: '- goalieTechnique: Describe the save technique used (e.g., "butterfly save", "glove save", "pad stack", "standing save")',
      wellDone: '- whatWentWell: What the goalie did correctly in this situation',
      improvement: '- areasForImprovement: What could have been done better',
      keyMoment: '- keyMoment: The critical moment in the play (include timestamp if specific, e.g., "at 02:47")',
      tip: '- coachingTip: Specific actionable advice for similar situations',
      important: 'IMPORTANT: Use timestamps in MM:SS format throughout. Be specific about goalie positioning, movement, and technique.',
      returnOnly: 'Return ONLY a valid JSON object with this exact structure (no additional text, no markdown formatting):',
      cannotAnalyze: 'If you cannot analyze the video or extract specific information, use null values for metrics and "Unknown" for text fields where appropriate.'
    },
    fr: {
      gameInfo: '1. **Informations du Match**',
      opponent: '- Nom de l\'équipe adverse (si visible sur les maillots/panneaux, sinon "Équipe Inconnue")',
      competition: '- Type de compétition: choisir parmi "regular-season", "playoff", "tournament", "exhibition", ou "practice"',
      performance: '2. **Métriques de Performance**',
      shotsFaced: '- Total des tirs affrontés par le gardien',
      saves: '- Nombre d\'arrêts effectués',
      goals: '- Calculer les buts accordés (tirs affrontés - arrêts)',
      outcome: '3. **Résultat du Match**',
      result: '- Résultat: "win", "loss", "win-ot", "loss-ot", "win-so", "loss-so", ou null si incertain',
      shotData: '4. **Données de Tirs avec Horodatage et Analyse** (pour chaque tentative de tir significative, jusqu\'à 10 des plus importantes - arrêts et buts)',
      timing: 'a) **Informations d\'Horodatage** (CRITIQUE - fournir les horodatages vidéo exacts)',
      clipStart: '- clipStartTime: horodatage exact au format MM:SS quand la tentative de tir commence (ex: "01:23", "05:47")',
      clipEnd: '- clipEndTime: horodatage exact au format MM:SS quand le jeu se termine (ex: "01:28", "05:52")',
      noZero: '- NE PAS utiliser "00:00" - fournir le temps réel dans la vidéo quand chaque tir se produit',
      shotDetails: 'b) **Détails du Tir**',
      shotType: '- Type de tir: choisir parmi "Wrist Shot", "Slap Shot", "Snap Shot", "Backhand", "Deflection / Tip-in", "One-Timer", "Wraparound", "Rebound Shot"',
      gameSituation: '- Situation de jeu: choisir parmi "Break away", "Penalty Shot", "Screened Shot", "Odd-Man Rush", "Cross-Ice Pass", "Point Shot", "Net-Front Battle", "Shorthanded Goal", "Power Play Goal", "Turnover Leading to Goal", "Off the Rush", "Puck Battle / Scramble"',
      resultType: '- Résultat: "save" ou "goal"',
      origin: '- Origine du tir: coordonnées x,y (échelle 0-100, où 0,0 est en haut à gauche de la patinoire du point de vue du gardien)',
      destination: '- Destination du tir: coordonnées x,y (échelle 0-100, où 0,0 est en haut à gauche du filet)',
      analysis: 'c) **Analyse Détaillée du Gardien** pour ce tir spécifique:',
      technique: '- goalieTechnique: Décrire la technique d\'arrêt utilisée (ex: "arrêt papillon", "arrêt gant", "empilement de jambières", "arrêt debout") - EN FRANÇAIS',
      wellDone: '- whatWentWell: Ce que le gardien a fait correctement dans cette situation - EN FRANÇAIS',
      improvement: '- areasForImprovement: Ce qui aurait pu être mieux fait - EN FRANÇAIS',
      keyMoment: '- keyMoment: Le moment critique du jeu (inclure l\'horodatage si spécifique, ex: "à 02:47") - EN FRANÇAIS',
      tip: '- coachingTip: Conseil actionnable spécifique pour des situations similaires - EN FRANÇAIS',
      important: 'IMPORTANT: Utiliser le format d\'horodatage MM:SS partout. Être spécifique sur le positionnement, le mouvement et la technique du gardien.',
      returnOnly: 'Retourner SEULEMENT un objet JSON valide avec cette structure exacte (pas de texte additionnel, pas de formatage markdown):',
      cannotAnalyze: 'Si vous ne pouvez pas analyser la vidéo ou extraire des informations spécifiques, utiliser des valeurs null pour les métriques et "Unknown" pour les champs texte le cas échéant.'
    }
  };
  
  const lang = instructions[language];
  
  return `${prompts[language]}

Based on your analysis of the provided video, provide the following information needed for goalie performance tracking:

${lang.gameInfo}
   ${lang.opponent}
   ${lang.competition}

${lang.performance}
   ${lang.shotsFaced}
   ${lang.saves}
   ${lang.goals}

${lang.outcome}
   ${lang.result}

${lang.shotData}
   
   For EACH shot, provide:
   
   ${lang.timing}
      ${lang.clipStart}
      ${lang.clipEnd}
      ${lang.noZero}
   
   ${lang.shotDetails}
      ${lang.shotType}
      ${lang.gameSituation}
      ${lang.resultType}
      ${lang.origin}
      ${lang.destination}
   
   ${lang.analysis}
      ${lang.technique}
      ${lang.wellDone}
      ${lang.improvement}
      ${lang.keyMoment}
      ${lang.tip}

${lang.important}

${lang.returnOnly}
{
  "opponent": "string",
  "competitionType": "regular-season|playoff|tournament|exhibition|practice",
  "shotsFaced": number or null,
  "saves": number or null,
  "outcome": "win|loss|win-ot|loss-ot|win-so|loss-so|null",
  "shots": [
    {
      "clipStartTime": "MM:SS",
      "clipEndTime": "MM:SS",
      "shotType": "string",
      "gameSituation": "string", 
      "result": "save|goal",
      "shotOriginX": number (0-100),
      "shotOriginY": number (0-100),
      "shotDestinationX": number (0-100),
      "shotDestinationY": number (0-100),
      "analysis": {
        "goalieTechnique": "string",
        "whatWentWell": "string",
        "areasForImprovement": "string",
        "keyMoment": "string",
        "coachingTip": "string"
      }
    }
  ]
}

${lang.cannotAnalyze}`;
};

// Version 2: Enhanced prompt with visual cues for goal vs save detection
export const createEnhancedAnalysisPrompt = (language: 'en' | 'fr' = 'en'): string => {
  const prompts = {
    en: `You are a professional hockey goalie coach analyzing video footage to extract game data. 

IMPORTANT: To accurately distinguish between goals and saves, look for these key visual indicators:
- **GOAL INDICATORS**: Red goal light illuminated behind the goalie, referee pointing to the net, players celebrating, goalie stops playing and looks defeated, puck visibly crosses the goal line
- **SAVE INDICATORS**: Goalie successfully stops the puck, continues playing position, no goal light, play continues without stoppage, goalie shows confident body language

CRITICAL NOTE: When analyzing goals that are being added to the system (especially in admin areas), these should be marked as "goal" in the result field, NOT "save". Only mark as "save" when the goalie successfully prevents the puck from entering the net.

IMPORTANT: Always interpret and log goal and shot locations from the goalie's perspective — never from the camera's point of view.

Goalie's Perspective Rule:
If the footage shows the shooter facing the camera and the goalie with their back to the camera, then the view is from the attacker's perspective.
In this case, mirror the coordinates horizontally so that the shot and goal locations are logged as if seen from the goalie's eyes, defending the net.

Camera Position Awareness:
• If the camera is behind the net, use the footage as-is — this is already the goalie's perspective.
• If the camera is from center ice, detect jersey numbers/facing directions to determine who is attacking, and flip coordinates accordingly.

Pay special attention to these cues when determining the "result" field for each shot attempt.`,
    fr: `Vous êtes un entraîneur professionnel de gardiens de but de hockey analysant des images vidéo pour extraire des données de match.

IMPORTANT: Pour distinguer avec précision les buts des arrêts, recherchez ces indicateurs visuels clés:
- **INDICATEURS DE BUT**: Lumière rouge de but allumée derrière le gardien, arbitre pointant vers le filet, joueurs célébrant, gardien arrête de jouer et semble découragé, rondelle traverse visiblement la ligne de but
- **INDICATEURS D'ARRÊT**: Gardien arrête avec succès la rondelle, continue en position de jeu, pas de lumière de but, le jeu continue sans arrêt, gardien montre un langage corporel confiant

NOTE CRITIQUE: Lors de l'analyse des buts ajoutés au système (en particulier dans les zones d'administration), ceux-ci doivent être marqués comme "goal" dans le champ résultat, PAS "save". Marquez uniquement comme "save" lorsque le gardien empêche avec succès la rondelle d'entrer dans le filet.

IMPORTANT: Interprétez et enregistrez toujours les emplacements des buts et des tirs du point de vue du gardien — jamais du point de vue de la caméra.

Règle de perspective du gardien:
Si les images montrent le tireur face à la caméra et le gardien de dos à la caméra, alors la vue est du point de vue de l'attaquant.
Dans ce cas, inversez les coordonnées horizontalement afin que les emplacements des tirs et des buts soient enregistrés comme s'ils étaient vus des yeux du gardien, défendant le filet.

Conscience de la position de la caméra:
• Si la caméra est derrière le filet, utilisez les images telles quelles — c'est déjà la perspective du gardien.
• Si la caméra est du centre de la glace, détectez les numéros de maillot/directions de face pour déterminer qui attaque, et inversez les coordonnées en conséquence.

Portez une attention particulière à ces indices lors de la détermination du champ "result" pour chaque tentative de tir.`
  };
  
  // Rest of the function follows the same structure as createOriginalAnalysisPrompt
  // but with the enhanced opening instruction
  const instructions = {
    en: {
      gameInfo: '1. **Game Information**',
      opponent: '- Opponent team name (if visible on jerseys/boards, otherwise "Unknown Team")',
      competition: '- Competition type: choose from "regular-season", "playoff", "tournament", "exhibition", or "practice"',
      performance: '2. **Performance Metrics**',
      shotsFaced: '- Total shots faced by the goalkeeper',
      saves: '- Number of saves made',
      goals: '- Calculate goals allowed (shots faced - saves)',
      outcome: '3. **Game Outcome**',
      result: '- Result: "win", "loss", "win-ot", "loss-ot", "win-so", "loss-so", or null if unclear',
      shotData: '4. **Shot Data with Timestamps and Analysis** - CRITICAL: Only analyze shots that actually occur in the video. Do not create fictional shots.\n\n**Video Length Guidelines:**\n- Short clips (under 30 seconds): Typically contain 1-3 actual shots maximum\n- Longer footage: May contain more shots, but only analyze those that clearly happen\n- Quality over quantity: Better to have fewer accurate shots than many inaccurate ones',
      timing: 'a) **Timing Information** (CRITICAL - provide exact video timestamps)',
      clipStart: '- clipStartTime: exact timestamp in MM:SS format when the shot attempt begins (e.g., "01:23", "05:47")',
      clipEnd: '- clipEndTime: exact timestamp in MM:SS format when the play ends (e.g., "01:28", "05:52")',
      noZero: '- DO NOT use "00:00" - provide the actual time in the video when each shot occurs',
      shotDetails: 'b) **Shot Details**',
      shotType: '- Shot type: choose from "Wrist Shot", "Slap Shot", "Snap Shot", "Backhand", "Deflection / Tip-in", "One-Timer", "Wraparound", "Rebound Shot"',
      gameSituation: '- Game situation: choose from "Break away", "Penalty Shot", "Screened Shot", "Odd-Man Rush", "Cross-Ice Pass", "Point Shot", "Net-Front Battle", "Shorthanded Goal", "Power Play Goal", "Turnover Leading to Goal", "Off the Rush", "Puck Battle / Scramble"',
      resultType: '- Result: "save" or "goal" (use the visual indicators mentioned above to determine this accurately)',
      origin: '- Shot origin: x,y coordinates (0-100 scale, where 0,0 is top-left of rink from goalie\'s perspective)',
      destination: '- Shot destination: x,y coordinates (0-100 scale, where 0,0 is top-left of goal net)',
      analysis: 'c) **Detailed Goalie Analysis** for this specific shot:',
      technique: '- goalieTechnique: Describe the save technique used (e.g., "butterfly save", "glove save", "pad stack", "standing save")',
      wellDone: '- whatWentWell: What the goalie did correctly in this situation',
      improvement: '- areasForImprovement: What could have been done better',
      keyMoment: '- keyMoment: The critical moment in the play (include timestamp if specific, e.g., "at 02:47")',
      tip: '- coachingTip: Specific actionable advice for similar situations',
      important: 'IMPORTANT: Use timestamps in MM:SS format throughout. Be specific about goalie positioning, movement, and technique. Pay extra attention to visual cues for accurate goal vs save classification.',
      returnOnly: 'Return ONLY a valid JSON object with this exact structure (no additional text, no markdown formatting):',
      cannotAnalyze: 'If you cannot analyze the video or extract specific information, use null values for metrics and "Unknown" for text fields where appropriate.'
    },
    fr: {
      gameInfo: '1. **Informations du Match**',
      opponent: '- Nom de l\'équipe adverse (si visible sur les maillots/panneaux, sinon "Équipe Inconnue")',
      competition: '- Type de compétition: choisir parmi "regular-season", "playoff", "tournament", "exhibition", ou "practice"',
      performance: '2. **Métriques de Performance**',
      shotsFaced: '- Total des tirs affrontés par le gardien',
      saves: '- Nombre d\'arrêts effectués',
      goals: '- Calculer les buts accordés (tirs affrontés - arrêts)',
      outcome: '3. **Résultat du Match**',
      result: '- Résultat: "win", "loss", "win-ot", "loss-ot", "win-so", "loss-so", ou null si incertain',
      shotData: '4. **Données de Tirs avec Horodatage et Analyse** - CRITIQUE: Analysez uniquement les tirs qui se produisent réellement dans la vidéo. Ne créez pas de tirs fictifs.\n\n**Directives de Durée Vidéo:**\n- Clips courts (moins de 30 secondes): Contiennent typiquement 1-3 tirs réels maximum\n- Séquences plus longues: Peuvent contenir plus de tirs, mais analysez seulement ceux qui se produisent clairement\n- Qualité plutôt que quantité: Mieux vaut avoir moins de tirs précis que beaucoup d\'imprécis',
      timing: 'a) **Informations d\'Horodatage** (CRITIQUE - fournir les horodatages vidéo exacts)',
      clipStart: '- clipStartTime: horodatage exact au format MM:SS quand la tentative de tir commence (ex: "01:23", "05:47")',
      clipEnd: '- clipEndTime: horodatage exact au format MM:SS quand le jeu se termine (ex: "01:28", "05:52")',
      noZero: '- NE PAS utiliser "00:00" - fournir le temps réel dans la vidéo quand chaque tir se produit',
      shotDetails: 'b) **Détails du Tir**',
      shotType: '- Type de tir: choisir parmi "Wrist Shot", "Slap Shot", "Snap Shot", "Backhand", "Deflection / Tip-in", "One-Timer", "Wraparound", "Rebound Shot"',
      gameSituation: '- Situation de jeu: choisir parmi "Break away", "Penalty Shot", "Screened Shot", "Odd-Man Rush", "Cross-Ice Pass", "Point Shot", "Net-Front Battle", "Shorthanded Goal", "Power Play Goal", "Turnover Leading to Goal", "Off the Rush", "Puck Battle / Scramble"',
      resultType: '- Résultat: "save" ou "goal" (utiliser les indicateurs visuels mentionnés ci-dessus pour déterminer cela avec précision)',
      origin: '- Origine du tir: coordonnées x,y (échelle 0-100, où 0,0 est en haut à gauche de la patinoire du point de vue du gardien)',
      destination: '- Destination du tir: coordonnées x,y (échelle 0-100, où 0,0 est en haut à gauche du filet)',
      analysis: 'c) **Analyse Détaillée du Gardien** pour ce tir spécifique:',
      technique: '- goalieTechnique: Décrire la technique d\'arrêt utilisée (ex: "arrêt papillon", "arrêt gant", "empilement de jambières", "arrêt debout") - EN FRANÇAIS',
      wellDone: '- whatWentWell: Ce que le gardien a fait correctement dans cette situation - EN FRANÇAIS',
      improvement: '- areasForImprovement: Ce qui aurait pu être mieux fait - EN FRANÇAIS',
      keyMoment: '- keyMoment: Le moment critique du jeu (inclure l\'horodatage si spécifique, ex: "à 02:47") - EN FRANÇAIS',
      tip: '- coachingTip: Conseil actionnable spécifique pour des situations similaires - EN FRANÇAIS',
      important: 'IMPORTANT: Utiliser le format d\'horodatage MM:SS partout. Être spécifique sur le positionnement, le mouvement et la technique du gardien. Porter une attention particulière aux indices visuels pour une classification précise but vs arrêt.',
      returnOnly: 'Retourner SEULEMENT un objet JSON valide avec cette structure exacte (pas de texte additionnel, pas de formatage markdown):',
      cannotAnalyze: 'Si vous ne pouvez pas analyser la vidéo ou extraire des informations spécifiques, utiliser des valeurs null pour les métriques et "Unknown" pour les champs texte le cas échéant.'
    }
  };
  
  const lang = instructions[language];
  
  return `${prompts[language]}

Based on your analysis of the provided video, provide the following information needed for goalie performance tracking:

${lang.gameInfo}
   ${lang.opponent}
   ${lang.competition}

${lang.performance}
   ${lang.shotsFaced}
   ${lang.saves}
   ${lang.goals}

${lang.outcome}
   ${lang.result}

${lang.shotData}
   
   For EACH shot, provide:
   
   ${lang.timing}
      ${lang.clipStart}
      ${lang.clipEnd}
      ${lang.noZero}
   
   ${lang.shotDetails}
      ${lang.shotType}
      ${lang.gameSituation}
      ${lang.resultType}
      ${lang.origin}
      ${lang.destination}
   
   ${lang.analysis}
      ${lang.technique}
      ${lang.wellDone}
      ${lang.improvement}
      ${lang.keyMoment}
      ${lang.tip}

${lang.important}

${lang.returnOnly}
{
  "opponent": "string",
  "competitionType": "regular-season|playoff|tournament|exhibition|practice",
  "shotsFaced": number or null,
  "saves": number or null,
  "outcome": "win|loss|win-ot|loss-ot|win-so|loss-so|null",
  "shots": [
    {
      "clipStartTime": "MM:SS",
      "clipEndTime": "MM:SS",
      "shotType": "string",
      "gameSituation": "string", 
      "result": "save|goal",
      "shotOriginX": number (0-100),
      "shotOriginY": number (0-100),
      "shotDestinationX": number (0-100),
      "shotDestinationY": number (0-100),
      "analysis": {
        "goalieTechnique": "string",
        "whatWentWell": "string",
        "areasForImprovement": "string",
        "keyMoment": "string",
        "coachingTip": "string"
      }
    }
  ]
}

${lang.cannotAnalyze}`;
};

// Version 3: Enhanced prompt with detailed shot location guidance
export const createShotLocationEnhancedPrompt = (language: 'en' | 'fr' = 'en'): string => {
  const prompts = {
    en: `You are a professional hockey goalie coach analyzing video footage to extract game data. 

IMPORTANT: To accurately distinguish between goals and saves, look for these key visual indicators:
- **GOAL INDICATORS**: Red goal light illuminated behind the goalie, referee pointing to the net, players celebrating, goalie stops playing and looks defeated, puck visibly crosses the goal line
- **SAVE INDICATORS**: Goalie successfully stops the puck, continues playing position, no goal light, play continues without stoppage, goalie shows confident body language

CRITICAL NOTE: When analyzing goals that are being added to the system (especially in admin areas), these should be marked as "goal" in the result field, NOT "save". Only mark as "save" when the goalie successfully prevents the puck from entering the net.

CRITICAL: SHOT LOCATION ACCURACY - Pay extremely close attention to shot positioning:

IMPORTANT: Always interpret and log goal and shot locations from the goalie's perspective — never from the camera's point of view.

Goalie's Perspective Rule:
If the footage shows the shooter facing the camera and the goalie with their back to the camera, then the view is from the attacker's perspective.

In this case, mirror the coordinates horizontally so that the shot and goal locations are logged as if seen from the goalie's eyes, defending the net.
• A goal on the shooter's left = goalie's blocker side (usually right-handed goalie but goalie can have the blocker on the left hand too).
• A shot from the shooter's right = shot from goalie's left defensive zone.

Camera Position Awareness:
• If the camera is behind the net, use the footage as-is — this is already the goalie's perspective.
• If the camera is from center ice, detect jersey numbers/facing directions to determine who is attacking, and flip coordinates accordingly.

DO NOT place shot or goal markers based solely on screen position.
The analysis must simulate the net that the goalie is defending, even if it appears flipped in the video.

**SHOT ORIGIN COORDINATES** (where the shot was taken from):
- Study the ice surface carefully and identify key landmarks (face-off circles, blue lines, red line, boards)
- The rink coordinate system: 0,0 is top-left corner from goalie's perspective, 100,100 is bottom-right
- **Left side of rink (from goalie's view)**: X coordinates 0-40
- **Center of rink**: X coordinates 40-60  
- **Right side of rink**: X coordinates 60-100
- **Behind goal line**: Y coordinates 0-10
- **Crease area**: Y coordinates 10-25
- **Slot area (high danger)**: Y coordinates 25-45
- **Point/blue line area**: Y coordinates 70-100

**SHOT DESTINATION COORDINATES** (where on the goal net):
- Goal net coordinate system: 0,0 is top-left corner of the goal opening
- **Top shelf**: Y coordinates 0-35
- **Middle net**: Y coordinates 35-65  
- **Bottom/five-hole**: Y coordinates 65-100
- **Glove side (goalie's left)**: X coordinates 0-45
- **Blocker side (goalie's right)**: X coordinates 55-100
- **Center/five-hole**: X coordinates 45-55

**VISUAL CUES TO HELP WITH POSITIONING**:
- Watch the shooter's position relative to face-off dots, goal posts, and other players
- Note the angle of the shot - sharp angle shots typically come from the sides (X < 30 or X > 70)
- Center ice shots are usually X coordinates 40-60
- Pay attention to camera angles and adjust coordinates accordingly
- If the shot comes from behind the goal line, Y coordinate should be very low (0-15)

Be methodical: pause at the moment the shot is released, identify the shooter's exact position on the ice using landmarks, then map that to the 0-100 coordinate system.

CRITICAL: Many broadcast angles are from behind or above the play and can cause optical distortion of the shooter's position.

Adjust shot origin estimation based on angle:
- If the camera is from the neutral zone (common broadcast view), players near the boards may appear closer to the center than they are.
- Use visible blue lines, faceoff dots, and goal crease as anchoring references for correcting depth perception.

If the camera angle switches sides (e.g., replay from opposite angle), REMEMBER to flip your X-coordinates accordingly.
- Original rink orientation: 0,0 = top-left from goalie's view
- Reversed angle: the goalie appears on the right — adjust X-coordinates: new X = 100 - observed X
Be consistent with rink orientation regardless of camera flips.

Wraparounds → start from behind net (Y ≈ 0-10, X ≈ 40-60)
Point Shots → usually near blue line (Y ≈ 80-100)
Net-front battles / rebounds → typically in crease (Y ≈ 10-25)
One-timers → frequently occur after cross-ice passes from side slots (X < 30 or X > 70, Y ≈ 30-50)

High-probability goals often originate in the slot (X = 35-65, Y = 25-45).
Pay extra attention to identifying shots from this zone — they are critical to goalie performance evaluation.

A player facing the boards is unlikely to be shooting. Re-evaluate if the shot is mistakenly registered from behind the net or from awkward body positioning.
Shooters usually plant their lead skate toward the net — use skate angle as a directional cue.

If a player receives a pass before the shot, follow the puck:
- Where the puck is received = likely shot origin
- Avoid placing origin where player is already gliding away from

ALWAYS timestamp the exact moment the puck leaves the blade (not when it's received, not when the goal is scored).

Use slow motion / frame-by-frame to pinpoint the release moment and ending frame (goalie contact, goal light, rebound).`,
    fr: `Vous êtes un entraîneur professionnel de gardiens de but de hockey analysant des images vidéo pour extraire des données de match.

IMPORTANT: Pour distinguer avec précision les buts des arrêts, recherchez ces indicateurs visuels clés:
- **INDICATEURS DE BUT**: Lumière rouge de but allumée derrière le gardien, arbitre pointant vers le filet, joueurs célébrant, gardien arrête de jouer et semble découragé, rondelle traverse visiblement la ligne de but
- **INDICATEURS D'ARRÊT**: Gardien arrête avec succès la rondelle, continue en position de jeu, pas de lumière de but, le jeu continue sans arrêt, gardien montre un langage corporel confiant

NOTE CRITIQUE: Lors de l'analyse des buts ajoutés au système (en particulier dans les zones d'administration), ceux-ci doivent être marqués comme "goal" dans le champ résultat, PAS "save". Marquez uniquement comme "save" lorsque le gardien empêche avec succès la rondelle d'entrer dans le filet.

CRITIQUE: PRÉCISION DE L'EMPLACEMENT DES TIRS - Portez une attention extrême au positionnement des tirs:

IMPORTANT: Interprétez et enregistrez toujours les emplacements des buts et des tirs du point de vue du gardien — jamais du point de vue de la caméra.

Règle de perspective du gardien:
Si les images montrent le tireur face à la caméra et le gardien de dos à la caméra, alors la vue est du point de vue de l'attaquant.

Dans ce cas, inversez les coordonnées horizontalement afin que les emplacements des tirs et des buts soient enregistrés comme s'ils étaient vus des yeux du gardien, défendant le filet.
• Un but à gauche du tireur = côté bouclier du gardien (généralement gardien droitier mais le gardien peut avoir le bouclier sur la main gauche aussi).
• Un tir de la droite du tireur = tir de la zone défensive gauche du gardien.

Conscience de la position de la caméra:
• Si la caméra est derrière le filet, utilisez les images telles quelles — c'est déjà la perspective du gardien.
• Si la caméra est du centre de la glace, détectez les numéros de maillot/directions de face pour déterminer qui attaque, et inversez les coordonnées en conséquence.

NE PAS placer les marqueurs de tir ou de but en se basant uniquement sur la position à l'écran.
L'analyse doit simuler le filet que le gardien défend, même s'il apparaît inversé dans la vidéo.

**COORDONNÉES D'ORIGINE DU TIR** (d'où le tir a été effectué):
- Étudiez soigneusement la surface de glace et identifiez les repères clés (cercles de mise au jeu, lignes bleues, ligne rouge, bandes)
- Le système de coordonnées de la patinoire: 0,0 est le coin supérieur gauche du point de vue du gardien, 100,100 est le coin inférieur droit
- **Côté gauche de la patinoire (vue du gardien)**: Coordonnées X 0-40
- **Centre de la patinoire**: Coordonnées X 40-60
- **Côté droit de la patinoire**: Coordonnées X 60-100
- **Derrière la ligne de but**: Coordonnées Y 0-10
- **Zone du demi-cercle**: Coordonnées Y 10-25
- **Zone de l'enclave (haute dangerosité)**: Coordonnées Y 25-45
- **Zone de la pointe/ligne bleue**: Coordonnées Y 70-100

**COORDONNÉES DE DESTINATION DU TIR** (où sur le filet):
- Système de coordonnées du filet: 0,0 est le coin supérieur gauche de l'ouverture du but
- **Haut du filet**: Coordonnées Y 0-35
- **Milieu du filet**: Coordonnées Y 35-65
- **Bas/entre les jambières**: Coordonnées Y 65-100
- **Côté gant (gauche du gardien)**: Coordonnées X 0-45
- **Côté bouclier (droite du gardien)**: Coordonnées X 55-100
- **Centre/entre les jambières**: Coordonnées X 45-55

**INDICES VISUELS POUR AIDER AU POSITIONNEMENT**:
- Observez la position du tireur par rapport aux points de mise au jeu, aux poteaux du but et aux autres joueurs
- Notez l'angle du tir - les tirs à angle aigu viennent généralement des côtés (X < 30 ou X > 70)
- Les tirs du centre de la glace sont généralement des coordonnées X 40-60
- Attention aux angles de caméra et ajustez les coordonnées en conséquence
- Si le tir vient de derrière la ligne de but, la coordonnée Y devrait être très basse (0-15)

Soyez méthodique: arrêtez-vous au moment où le tir est libéré, identifiez la position exacte du tireur sur la glace en utilisant des repères, puis cartographiez cela sur le système de coordonnées 0-100.

CRITIQUE: De nombreux angles de diffusion sont pris de derrière ou au-dessus du jeu et peuvent causer une distorsion optique de la position du tireur.

Ajustez l'estimation de l'origine du tir selon l'angle:
- Si la caméra est de la zone neutre (vue de diffusion commune), les joueurs près des bandes peuvent sembler plus près du centre qu'ils ne le sont.
- Utilisez les lignes bleues visibles, les points de mise au jeu et le demi-cercle comme références d'ancrage pour corriger la perception de profondeur.

Si l'angle de caméra change de côté (ex: reprise de l'angle opposé), RAPPELEZ-VOUS de retourner vos coordonnées X en conséquence.
- Orientation originale de la patinoire: 0,0 = coin supérieur gauche de la vue du gardien
- Angle inversé: le gardien apparaît à droite — ajustez les coordonnées X: nouveau X = 100 - X observé
Soyez cohérent avec l'orientation de la patinoire indépendamment des retournements de caméra.

Contournements → commencent de derrière le filet (Y ≈ 0-10, X ≈ 40-60)
Tirs de la pointe → généralement près de la ligne bleue (Y ≈ 80-100)
Batailles devant le filet / rebonds → typiquement dans le demi-cercle (Y ≈ 10-25)
Une-temps → se produisent fréquemment après des passes transversales depuis les slots latéraux (X < 30 ou X > 70, Y ≈ 30-50)

Les buts à haute probabilité proviennent souvent de l'enclave (X = 35-65, Y = 25-45).
Portez une attention particulière à identifier les tirs de cette zone — ils sont critiques pour l'évaluation de la performance du gardien.

Un joueur face aux bandes est peu susceptible de tirer. Réévaluez si le tir est incorrectement enregistré depuis derrière le filet ou depuis une position corporelle awkward.
Les tireurs plantent généralement leur patin directeur vers le filet — utilisez l'angle du patin comme indice directionnel.

Si un joueur reçoit une passe avant le tir, suivez la rondelle:
- Où la rondelle est reçue = origine probable du tir
- Évitez de placer l'origine où le joueur glisse déjà loin de

TOUJOURS horodater le moment exact où la rondelle quitte la palette (pas quand elle est reçue, pas quand le but est marqué).

Utilisez le ralenti / image par image pour identifier le moment de libération et la trame finale (contact du gardien, lumière de but, rebond).`
  };
  
  // Use the same instruction structure as the enhanced prompt but with updated coordinate guidance
  const instructions = {
    en: {
      gameInfo: '1. **Game Information**',
      opponent: '- Opponent team name (if visible on jerseys/boards, otherwise "Unknown Team")',
      competition: '- Competition type: choose from "regular-season", "playoff", "tournament", "exhibition", or "practice"',
      performance: '2. **Performance Metrics**',
      shotsFaced: '- Total shots faced by the goalkeeper',
      saves: '- Number of saves made',
      goals: '- Calculate goals allowed (shots faced - saves)',
      outcome: '3. **Game Outcome**',
      result: '- Result: "win", "loss", "win-ot", "loss-ot", "win-so", "loss-so", or null if unclear',
      shotData: '4. **Shot Data with Timestamps and Analysis** - CRITICAL: Only analyze shots that actually occur in the video. Do not create fictional shots.\n\n**Video Length Guidelines:**\n- Short clips (under 30 seconds): Typically contain 1-3 actual shots maximum\n- Longer footage: May contain more shots, but only analyze those that clearly happen\n- Quality over quantity: Better to have fewer accurate shots than many inaccurate ones',
      timing: 'a) **Timing Information** (CRITICAL - provide exact video timestamps)',
      clipStart: '- clipStartTime: exact timestamp in MM:SS format when the shot attempt begins (e.g., "01:23", "05:47")',
      clipEnd: '- clipEndTime: exact timestamp in MM:SS format when the play ends (e.g., "01:28", "05:52")',
      noZero: '- DO NOT use "00:00" - provide the actual time in the video when each shot occurs',
      shotDetails: 'b) **Shot Details**',
      shotType: '- Shot type: choose from "Wrist Shot", "Slap Shot", "Snap Shot", "Backhand", "Deflection / Tip-in", "One-Timer", "Wraparound", "Rebound Shot"',
      gameSituation: '- Game situation: choose from "Break away", "Penalty Shot", "Screened Shot", "Odd-Man Rush", "Cross-Ice Pass", "Point Shot", "Net-Front Battle", "Shorthanded Goal", "Power Play Goal", "Turnover Leading to Goal", "Off the Rush", "Puck Battle / Scramble"',
      resultType: '- Result: "save" or "goal" (use the visual indicators mentioned above to determine this accurately)',
      origin: '- Shot origin: x,y coordinates (0-100 scale, where 0,0 is top-left of rink from goalie\'s perspective) - **CRITICAL**: Use the detailed coordinate guidance above to be extremely precise',
      destination: '- Shot destination: x,y coordinates (0-100 scale, where 0,0 is top-left of goal net) - **CRITICAL**: Use the goal net zones described above for accuracy',
      analysis: 'c) **Detailed Goalie Analysis** for this specific shot:',
      technique: '- goalieTechnique: Describe the save technique used (e.g., "butterfly save", "glove save", "pad stack", "standing save")',
      wellDone: '- whatWentWell: What the goalie did correctly in this situation',
      improvement: '- areasForImprovement: What could have been done better',
      keyMoment: '- keyMoment: The critical moment in the play (include timestamp if specific, e.g., "at 02:47")',
      tip: '- coachingTip: Specific actionable advice for similar situations',
      important: 'CRITICAL: Use timestamps in MM:SS format throughout. Be EXTREMELY precise about shot origin and destination coordinates using the detailed guidance provided above. Study the ice surface landmarks carefully.',
      returnOnly: 'Return ONLY a valid JSON object with this exact structure (no additional text, no markdown formatting):',
      cannotAnalyze: 'If you cannot analyze the video or extract specific information, use null values for metrics and "Unknown" for text fields where appropriate.'
    },
    fr: {
      gameInfo: '1. **Informations du Match**',
      opponent: '- Nom de l\'équipe adverse (si visible sur les maillots/panneaux, sinon "Équipe Inconnue")',
      competition: '- Type de compétition: choisir parmi "regular-season", "playoff", "tournament", "exhibition", ou "practice"',
      performance: '2. **Métriques de Performance**',
      shotsFaced: '- Total des tirs affrontés par le gardien',
      saves: '- Nombre d\'arrêts effectués',
      goals: '- Calculer les buts accordés (tirs affrontés - arrêts)',
      outcome: '3. **Résultat du Match**',
      result: '- Résultat: "win", "loss", "win-ot", "loss-ot", "win-so", "loss-so", ou null si incertain',
      shotData: '4. **Données de Tirs avec Horodatage et Analyse** - CRITIQUE: Analysez uniquement les tirs qui se produisent réellement dans la vidéo. Ne créez pas de tirs fictifs.\n\n**Directives de Durée Vidéo:**\n- Clips courts (moins de 30 secondes): Contiennent typiquement 1-3 tirs réels maximum\n- Séquences plus longues: Peuvent contenir plus de tirs, mais analysez seulement ceux qui se produisent clairement\n- Qualité plutôt que quantité: Mieux vaut avoir moins de tirs précis que beaucoup d\'imprécis',
      timing: 'a) **Informations d\'Horodatage** (CRITIQUE - fournir les horodatages vidéo exacts)',
      clipStart: '- clipStartTime: horodatage exact au format MM:SS quand la tentative de tir commence (ex: "01:23", "05:47")',
      clipEnd: '- clipEndTime: horodatage exact au format MM:SS quand le jeu se termine (ex: "01:28", "05:52")',
      noZero: '- NE PAS utiliser "00:00" - fournir le temps réel dans la vidéo quand chaque tir se produit',
      shotDetails: 'b) **Détails du Tir**',
      shotType: '- Type de tir: choisir parmi "Wrist Shot", "Slap Shot", "Snap Shot", "Backhand", "Deflection / Tip-in", "One-Timer", "Wraparound", "Rebound Shot"',
      gameSituation: '- Situation de jeu: choisir parmi "Break away", "Penalty Shot", "Screened Shot", "Odd-Man Rush", "Cross-Ice Pass", "Point Shot", "Net-Front Battle", "Shorthanded Goal", "Power Play Goal", "Turnover Leading to Goal", "Off the Rush", "Puck Battle / Scramble"',
      resultType: '- Résultat: "save" ou "goal" (utiliser les indicateurs visuels mentionnés ci-dessus pour déterminer cela avec précision)',
      origin: '- Origine du tir: coordonnées x,y (échelle 0-100, où 0,0 est en haut à gauche de la patinoire du point de vue du gardien) - **CRITIQUE**: Utiliser les directives de coordonnées détaillées ci-dessus pour être extrêmement précis',
      destination: '- Destination du tir: coordonnées x,y (échelle 0-100, où 0,0 est en haut à gauche du filet) - **CRITIQUE**: Utiliser les zones du filet décrites ci-dessus pour la précision',
      analysis: 'c) **Analyse Détaillée du Gardien** pour ce tir spécifique:',
      technique: '- goalieTechnique: Décrire la technique d\'arrêt utilisée (ex: "arrêt papillon", "arrêt gant", "empilement de jambières", "arrêt debout") - EN FRANÇAIS',
      wellDone: '- whatWentWell: Ce que le gardien a fait correctement dans cette situation - EN FRANÇAIS',
      improvement: '- areasForImprovement: Ce qui aurait pu être mieux fait - EN FRANÇAIS',
      keyMoment: '- keyMoment: Le moment critique du jeu (inclure l\'horodatage si spécifique, ex: "à 02:47") - EN FRANÇAIS',
      tip: '- coachingTip: Conseil actionnable spécifique pour des situations similaires - EN FRANÇAIS',
      important: 'CRITIQUE: Utiliser le format d\'horodatage MM:SS partout. Être EXTRÊMEMENT précis sur les coordonnées d\'origine et de destination du tir en utilisant les directives détaillées fournies ci-dessus. Étudier soigneusement les repères de la surface de glace.',
      returnOnly: 'Retourner SEULEMENT un objet JSON valide avec cette structure exacte (pas de texte additionnel, pas de formatage markdown):',
      cannotAnalyze: 'Si vous ne pouvez pas analyser la vidéo ou extraire des informations spécifiques, utiliser des valeurs null pour les métriques et "Unknown" pour les champs texte le cas échéant.'
    }
  };
  
  const lang = instructions[language];
  
  return `${prompts[language]}

Based on your analysis of the provided video, provide the following information needed for goalie performance tracking:

${lang.gameInfo}
   ${lang.opponent}
   ${lang.competition}

${lang.performance}
   ${lang.shotsFaced}
   ${lang.saves}
   ${lang.goals}

${lang.outcome}
   ${lang.result}

${lang.shotData}
   
   For EACH shot, provide:
   
   ${lang.timing}
      ${lang.clipStart}
      ${lang.clipEnd}
      ${lang.noZero}
   
   ${lang.shotDetails}
      ${lang.shotType}
      ${lang.gameSituation}
      ${lang.resultType}
      ${lang.origin}
      ${lang.destination}
   
   ${lang.analysis}
      ${lang.technique}
      ${lang.wellDone}
      ${lang.improvement}
      ${lang.keyMoment}
      ${lang.tip}

${lang.important}

${lang.returnOnly}
{
  "opponent": "string",
  "competitionType": "regular-season|playoff|tournament|exhibition|practice",
  "shotsFaced": number or null,
  "saves": number or null,
  "outcome": "win|loss|win-ot|loss-ot|win-so|loss-so|null",
  "shots": [
    {
      "clipStartTime": "MM:SS",
      "clipEndTime": "MM:SS",
      "shotType": "string",
      "gameSituation": "string", 
      "result": "save|goal",
      "shotOriginX": number (0-100),
      "shotOriginY": number (0-100),
      "shotDestinationX": number (0-100),
      "shotDestinationY": number (0-100),
      "analysis": {
        "goalieTechnique": "string",
        "whatWentWell": "string",
        "areasForImprovement": "string",
        "keyMoment": "string",
        "coachingTip": "string"
      }
    }
  ]
}

${lang.cannotAnalyze}`;
};

// Array of all available prompt generators
export const promptGenerators = [
  {
    name: 'original',
    version: '1.0.0',
    description: 'Original video analysis prompt',
    generator: createOriginalAnalysisPrompt
  },
  {
    name: 'enhanced',
    version: '2.0.0', 
    description: 'Enhanced prompt with visual cues for goal vs save detection',
    generator: createEnhancedAnalysisPrompt
  },
  {
    name: 'shot-location-enhanced',
    version: '3.0.0',
    description: 'Enhanced prompt with detailed shot location guidance for improved coordinate accuracy',
    generator: createShotLocationEnhancedPrompt
  }
];

// Current active prompt generator (can be easily changed)
export const getCurrentPromptGenerator = () => {
  return createShotLocationEnhancedPrompt; // Using the new shot location enhanced version
};